import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { bgImgUpload } from '../../api/firebase/firestore/company';
import { clientRegisterCompanySchema } from '../../api/firebase/firestore/company/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  black,
  blackText,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground
} from '../../constants/Colors';
import {
  buttonFont,
  sizeBelowTenMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';

const styles = {
  button: {
    '@media screen and (max-width:480px)': {
      fontSize: '13px'
    },
    height: '30px',
    width: '100px'
  },
  buttonFont,
  card: {
    '@media screen and (max-width:480px)': {
      padding: '18px',
      width: '85%'
    },
    bgcolor: 'background.paper',
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    left: '50%',
    p: 4,
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  imgCircle: {
    '@media screen and (max-width:480px)': {
      width: '70px'
    },
    borderRadius: '4px',
    height: '50px',
    marginBottom: '10px',
    width: '100px'
  },
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    '@media screen and (max-width:480px)': {
      fontSize: '13px',
      padding: '6px 8px',
      width: '138px'
    },
    backgroundColor: black,
    height: '36px',
    width: '157px'
  }
};

interface Props {
  open: boolean;
  handleOpen: VoidFunction;
  handleClose: VoidFunction;
  setImage: React.Dispatch<React.SetStateAction<string>>;
}

export interface BackgroundType {
  image: File;
}

export default function BackgroundEditModal(props: Props): JSX.Element {
  const { open, handleClose, setImage } = props;
  const { t } = useTranslation();

  const {
    control,
    formState: { errors }
  } = useForm<BackgroundType>({
    mode: 'onChange',
    resolver: yupResolver(clientRegisterCompanySchema)
  });

  const [preview, setPreview] = useState('');
  const [s3File, setS3File] = useState<FileList | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { currentUser } = useContext(AuthContext);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const userRef = doc(usersRef, currentUser?.uid ?? '');
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        await bgImgUpload(userSnap, s3File);
        const blob = new Blob([s3File ? s3File[0] : '']);
        setImage(window.URL.createObjectURL(blob));
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const changeUploadFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreview(window.URL.createObjectURL(blob));
      setS3File(file);
    }
  };

  const [sizeError, setSizeError] = useState<boolean>(false);

  useEffect(() => {
    if (s3File && s3File.length > 0) {
      setSizeError(sizeBelowTenMb(s3File[0].size));
    } else {
      setSizeError(false);
    }
  }, [s3File]);

  return (
    <>
      <CustomLoading loading={loading} />
      <Modal open={open} onClose={handleClose}>
        <Box sx={styles.card}>
          <Typography sx={[styles.titleMb, styles.title]} component="div">
            {t('orientation.bgImage')}
          </Typography>
          <Grid
            container
            justifyContent={'flex-end'}
            sx={{ alignItems: 'center' }}
          >
            <Grid item xs={5} sm={6}>
              {preview ? (
                <CardMedia image={preview} sx={styles.imgCircle} />
              ) : (
                <Typography
                  sx={{
                    color: black,
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap'
                  }}
                  component="span"
                >
                  {`${t('orientation.bgImageUpload')}\n${t(
                    'orientation.recommendSize'
                  )}`}
                </Typography>
              )}
            </Grid>
            <Grid item xs={7} sm={6} sx={{ textAlign: 'right' }}>
              <Button
                component="label"
                variant="contained"
                sx={styles.uploadButton}
              >
                <>
                  <Controller
                    control={control}
                    name="image"
                    render={({ field }) => (
                      <input
                        accept=".jpg, .jpeg, .png, .webp, .gif, .svg"
                        type="file"
                        hidden
                        onChange={(value) => {
                          field.onChange(value);
                          changeUploadFile(value);
                        }}
                      />
                    )}
                  />
                  <AddIcon sx={{ color: '#FFF' }} />
                  {t('form.logoUploadButton')}
                </>
              </Button>
            </Grid>
          </Grid>
          <FormHelperText sx={styles.helper} error={Boolean(errors.image)}>
            {errors.image?.message}
          </FormHelperText>
          <FormHelperText sx={styles.helper} error={sizeError}>
            {sizeError && t('validation.error.imageSize')}
          </FormHelperText>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <Grid container justifyContent="flex-end">
              <Grid>
                <Button
                  onClick={handleClose}
                  sx={[
                    styles.button,
                    secondaryButtonnBackground,
                    { height: '42px', mr: '8px', p: 0 }
                  ]}
                >
                  <Typography
                    component="div"
                    color={primarycolor}
                    sx={styles.buttonFont}
                  >
                    {t('form.imageUploadCancel')}
                  </Typography>
                </Button>
                <Button
                  sx={[styles.button, primaryButtonBackground, { p: 0 }]}
                  disabled={sizeError}
                  onClick={() => {
                    void (async () => {
                      await onSubmit();
                    })();
                  }}
                >
                  <Typography
                    component="div"
                    color={'white'}
                    sx={styles.buttonFont}
                  >
                    {t('form.imageUpload')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Typography>
        </Box>
      </Modal>
    </>
  );
}
